import { orderBy } from 'lodash-es'
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'

import { FvButton, TextAreaField, ValidatedForm } from '@fv/client-components'

import { EmptyList } from '@/components/shared/EmptyList'
import type { Opportunity } from '@/types/Opportunity'

import { MessageRow } from './MessageRow'
import { useMessages, useSendMessage } from './queries'

type Props = {
  opportunity: Opportunity
}

export const MessagingPanel = ({ opportunity }: Props) => {
  const [fields, setFields] = useState({ text: '' })
  const formRef = useRef<HTMLFormElement>(null)
  const { isCanceled, loadId } = opportunity
  const messagesQuery = useMessages({ loadId })
  const { sendMessage, isSendingMessage } = useSendMessage(loadId)

  useEffect(() => {
    if (!loadId) return
    setFields({ text: '' })
    formRef.current?.reset()
  }, [loadId])

  const messages = useMemo(() => {
    if (!messagesQuery.data) return []

    return orderBy(
      messagesQuery.data.map(m => ({
        ...m,
        author: m.participants.find(p => p.isAuthor),
      })),
      m => new Date(m.createdDate),
      'desc',
    )
  }, [messagesQuery.data])

  async function onValidSubmit() {
    sendMessage({ messageText: fields.text.trim() })
    resetForm()
  }

  function onFieldChanged(e: ChangeEvent<HTMLTextAreaElement>) {
    const name = e.target.name
    const value = e.target.value
    setFields(f => ({ ...f, [name]: value }))
  }

  function resetForm() {
    setFields({ text: '' })
    formRef.current?.reset()
  }

  if (messagesQuery.isLoading) {
    return (
      <EmptyList
        busy={messagesQuery.isFetching}
        message="Loading messages..."
      />
    )
  }

  return (
    <>
      {!isCanceled && (
        <ValidatedForm ref={formRef} onValidSubmit={onValidSubmit}>
          <div className="mb-4 flex">
            <TextAreaField
              className="form-control min-h-[2.8rem]"
              disabled={isSendingMessage}
              maxLength={500}
              name="text"
              onChange={onFieldChanged}
              required
              rows={1}
              value={fields.text}
              autoExpand
            />
            <FvButton
              theme="default"
              disabled={isSendingMessage}
              type="submit"
              className="-ml-px"
              icon={isSendingMessage ? 'spinner' : 'paper-plane'}
            >
              <span>Send</span>
            </FvButton>
          </div>
        </ValidatedForm>
      )}

      {messages.length ? (
        messages.map(m => <MessageRow key={m._id} message={m} />)
      ) : (
        <EmptyList message="No messages available." />
      )}
    </>
  )
}
